import { ArtworkImage } from './ArtworkImage';
import { ItemPurchaseContext, useItemWeaponPreview } from './ItemPresentationContext';
import React, { useEffect, useState } from 'react';
import { GameDialog } from './GameDialog';
import { InvestigatorInspector } from './InvestigatorInspector';
import { InvestigatorSummary } from './GameStatusSummary';
import { Investigator } from '../api';
import type { Herald, Monster, MythosBreakdown as MythosBreakdownType, UserAction } from '../api';
import { Card, getItemImagePath, getDescription } from './Card';
import type { StackedItemWithKind } from './Card';
import { getDigitalItemArtworkPath } from '../utils/itemArtwork';
import { SpellActionPreviewContext, SpellInvestigatorContext, useSpellCastingPreview } from './SpellCastingContext';
import { getItemKindFromCardType } from './StackedItemCard';
import { MythosBreakdown, TerrorLevelIncrease } from './MythosBreakdown';
import { HeraldCard } from './HeraldCard';
import { MonsterInspector } from './MonsterInspector';
import { MonsterDetailCard } from './Monsters';
import { GuardianCard } from './GuardianCard';
import { InstitutionDisplay } from './InstitutionDisplay';
import type { Institution } from '../api';
import { BastToken } from './BastToken';
import { InvestigatorMarker } from './InvestigatorMarker';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { getAncientOnePreviewImagePath } from './AncientOneDigitalCard';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { SimpleMarkdown } from './SimpleMarkdown';
import { digitalCardDescription } from '../utils/digitalCardDescription';
import { getInvestigatorTokenImagePath } from '../utils/investigatorArtwork';
import './CompactActions.css';
import { ActionTypeIcon, actionIconKind } from './ActionTypeIcon';

const MOBILE_ACTIONS_QUERY = '(max-width: 767px), (max-width: 1024px) and (max-height: 500px)';

const ActionCardDescription: React.FC<{ card: StackedItemWithKind; digital: boolean }> = ({ card, digital }) => {
  const weapon = useItemWeaponPreview(card.kind, (card.item as { id?: string }).id);
  const spell = useSpellCastingPreview(card.kind === 'spell' ? (card.item as { id?: string }).id : undefined);
  const item = digital && weapon ? { ...card.item, type: weapon.effectiveType } : card.item;
  let description = getDescription({ ...card, item });
  if (digital) description = digitalCardDescription(card.kind, card.item.name, description);
  const modifier = spell?.currentCastingModifier;
  if (digital && card.kind === 'spell' && (card.item as { castingModifier?: number }).castingModifier === 99 && modifier != null) {
    description = description.replace('*Modifier*: **X**', `*Modifier*: **${modifier < 0 ? `−${Math.abs(modifier)}` : `+${modifier}`}**`);
  }
  return <SimpleMarkdown text={description} keyPrefix="action-item-description" wrapperClassName="action-description" />;
};

type MythosCardActionPayload = {
  name?: string;
  type?: string;
  processType?: string | null;
  clueAppearsAtLocation?: string[] | null;
  gateOpensAt?: string | null;
  gateBurst?: boolean;
  doomTokensAdded?: number;
  monsterMovements?: Array<{ dimensionSign?: string; color?: string }> | null;
  textDescription?: string;
  rumorPassCondition?: string | null;
  rumorPassReward?: string | null;
  rumorFailCondition?: string | null;
  rumorFailPenalty?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getCardName = (card: unknown): string | undefined => {
  if (Array.isArray(card)) return card.map(getCardName).filter(Boolean).join(' + ') || undefined;
  if (!isRecord(card)) {
    return undefined;
  }

  return typeof card.name === 'string' ? card.name : undefined;
};

const getChoiceDescription = (card: unknown): string => {
  if (!isRecord(card)) return '';
  for (const key of ['textDescription', 'powerDescription', 'description']) {
    if (typeof card[key] === 'string') return card[key] as string;
  }
  return '';
};

const getClueCountFromCard = (card: unknown): number => {
  if (typeof card === 'number' && Number.isFinite(card)) {
    return Math.floor(card);
  }

  if (typeof card === 'string') {
    const parsed = Number(card);
    if (Number.isFinite(parsed)) {
      return Math.floor(parsed);
    }
  }

  if (isRecord(card)) {
    const directCount = card.count;
    if (typeof directCount === 'number' && Number.isFinite(directCount)) {
      return Math.floor(directCount);
    }
  }

  return 1;
};

const getMythosEffect = (card: MythosCardActionPayload): string => {
  const effectText = card.textDescription?.trim() || card.name || '';
  switch (card.type) {
    case 'Headline':
      return `Headline: ${effectText}`;
    case 'Process':
      return card.processType ? `Process (${card.processType}): ${effectText}` : `Process: ${effectText}`;
    case 'Rumor':
      return `Rumor: ${effectText}`;
    case 'Shuffle':
      return 'Shuffle Mythos: shuffle the Mythos deck.';
    default:
      return effectText;
  }
};

const toMythosBreakdown = (card: unknown): MythosBreakdownType | null => {
  if (!isRecord(card)) {
    return null;
  }

  if ('gate' in card && 'clues' in card) {
    return card as MythosBreakdownType;
  }

  const mythosCard = card as MythosCardActionPayload;
  const clueLocations = Array.isArray(mythosCard.clueAppearsAtLocation)
    ? mythosCard.clueAppearsAtLocation.filter((location): location is string => typeof location === 'string' && location.trim().length > 0)
    : [];
  const gateLocationName = typeof mythosCard.gateOpensAt === 'string' && mythosCard.gateOpensAt.trim().length > 0
    ? mythosCard.gateOpensAt
    : null;
  const doomTokensAdded = Math.max(0, Number(mythosCard.doomTokensAdded ?? 0));
  const monsterMovement = Array.isArray(mythosCard.monsterMovements)
    ? mythosCard.monsterMovements
      .filter((movement): movement is { dimensionSign: string; color: string } =>
        !!movement &&
        typeof movement.dimensionSign === 'string' &&
        movement.dimensionSign.trim().length > 0 &&
        typeof movement.color === 'string' &&
        movement.color.trim().length > 0
      )
    : [];

  const gateResults: MythosBreakdownType['gate'] = [];
  if (gateLocationName) {
    gateResults.push({
      result: mythosCard.gateBurst ? 'Burst Open' : 'Open',
      location: { name: gateLocationName } as any,
      isBurst: Boolean(mythosCard.gateBurst),
    });
  } else if (doomTokensAdded <= 0) {
    gateResults.push({
      result: 'Blocked',
      isBurst: false,
    });
  }

  if (doomTokensAdded > 0) {
    gateResults.push({
      result: 'DoomTokenIncrease',
      doomTokensAdded,
      isBurst: false,
    });
  }

  return {
    name: mythosCard.name,
    gate: gateResults,
    clues: clueLocations.map((locationName) => ({
      result: 'Location',
      blocked: false,
      location: { name: locationName } as any,
    })),
    appearedMonsters: [],
    movedMonsters: [],
    terrorLevelIncrease: 0,
    doomTrackIncrease: doomTokensAdded,
    monsterMovement,
    effect: getMythosEffect(mythosCard),
    rumorPassCondition: mythosCard.rumorPassCondition,
    rumorPassReward: mythosCard.rumorPassReward,
    rumorFailCondition: mythosCard.rumorFailCondition,
    rumorFailPenalty: mythosCard.rumorFailPenalty,
  };
};

interface ActionsListProps {
  compact?: boolean;
  getActionLabel?: (action: UserAction) => string;
  previewOnly?: boolean;
  hideActionButton?: boolean;
  language?: string;
  actions: UserAction[];
  loading: boolean;
  onExecuteAction: (actionId: string) => void;
}

export const ActionsList: React.FC<ActionsListProps> = ({ actions, loading, onExecuteAction, previewOnly, hideActionButton = false, language, compact = false, getActionLabel }) => {
  const digital = useMonsterTokenStyle() === 'Digital';
  const [selectedDetails, setDetails] = useState<UserAction | null>(null);
  const currentDetailsAction = selectedDetails && actions.find(action => action.id === selectedDetails.id);
  const details = selectedDetails && (currentDetailsAction
    ?? (selectedDetails.cardType === 'Monster' ? null : { ...selectedDetails, spellCastingPreview: undefined }));
  useEffect(() => {
    if (selectedDetails?.cardType === 'Monster' && !actions.some(action => action.id === selectedDetails.id)) setDetails(null);
  }, [actions, selectedDetails]);
  const [mobile, setMobile] = useState(() => window.matchMedia?.(MOBILE_ACTIONS_QUERY).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_ACTIONS_QUERY);
    if (!media) return;
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  if ((!actions || actions.length === 0) && !details) {
    return null;
  }

  const investigatorSelection = actions.some((a) => a.cardType === 'Investigator');

  return (
    <div className={`actions-section${compact && !previewOnly ? ' compact-actions' : ''}`}>
      <div className={`${investigatorSelection ? 'actions-grid-investigator' : 'actions-grid'}${actions.some(action => /^(swap_|set_)/.test(action.id)) ? ' game-skill-actions' : ''}`}>
        {actions.map((action, idx) => {
          const mythosDetails = action.cardType === 'MythosCard'
            ? toMythosBreakdown(action.card)
            : null;
          const singleCard = Array.isArray(action.card) || typeof action.card === 'number' ? null : action.card;
          const cardName = getCardName(action.card);
          const ancientOneThumbnail = action.cardType === 'AncientOne' && cardName?.trim()
            ? getAncientOnePreviewImagePath(cardName) : undefined;
          const normalizedCardName = cardName?.replace(/\s+/g, '').replace(/\./g, '').replaceAll("'", '').replaceAll('"', '');
          const cardImage = action.cardType
            ? {
                AncientOne: normalizedCardName ? `/AncientOnes/${encodeURIComponent(normalizedCardName)}.png` : undefined,
              }[action.cardType]
            : undefined;
          const heralds = action.cardType === 'Herald'
            ? (Array.isArray(action.card) ? action.card : [action.card])
                .filter((card): card is Herald => !!card && isRecord(card) && typeof card.name === 'string')
            : [];
          const guardian = action.cardType === 'Guardian'
            && action.card
            && isRecord(action.card)
            && typeof singleCard?.name === 'string'
              ? action.card as Herald
              : null;
          const clues = action.cardType === 'Clue'
            ? getClueCountFromCard(action.card)
            : 0;
          const bastTokenNumber = action.cardType === 'BastToken' ? getClueCountFromCard(action.card) : 0;
          const terrorLevel = action.cardType === 'TerrorLevel' && typeof action.card === 'number'
            && Number.isFinite(action.card) ? Math.max(0, Math.floor(action.card)) : 0;

          const colorStyle = action.color
            ? ({ ['--action-color' as '--action-color']: action.color } as React.CSSProperties)
            : undefined;
          const kind = getItemKindFromCardType(action.cardType);
          const setupChoice = ['Investigator', 'AncientOne', 'Herald', 'Guardian'].includes(action.cardType ?? '');
          const compactCard = compact && !previewOnly && !setupChoice && !!action.card && (!!kind || action.cardType === 'Monster');
          const actionLabel = getActionLabel?.(action) ?? (action.description || action.name);
          const selectionInvestigator = action.cardType === 'Investigator' && action.card ? action.card as Investigator : null;
          const fullInvestigatorSheet = !!selectionInvestigator && (digital || !mobile);
          const disabled = loading || !!action.disabled || !!previewOnly;
          const selectPreview = (event: React.MouseEvent<HTMLDivElement>) => {
            if (disabled || event.defaultPrevented || !event.currentTarget.contains(event.target as Node)) return;
            const target = event.target as Element;
            if (target.closest('button, a, input, select, textarea, [role="button"], [role="link"]')) return;
            event.stopPropagation();
            onExecuteAction(action.id);
          };

          return (
            <article className={`game-action-choice${setupChoice ? ' setup-selection-choice' : ''}${compactCard ? ' compact-card-choice' : ''}${fullInvestigatorSheet ? ' investigator-sheet-choice' : ''} ${/^(swap_|set_)/.test(action.id) ? 'game-skill-choice' : ''}`} key={`${action.id}_${idx}`}>
            <div className={`action-card-wrapper${fullInvestigatorSheet ? '' : ' action-selection-surface'}`}
              style={colorStyle}
              aria-disabled={disabled}
              onClick={fullInvestigatorSheet ? undefined : selectPreview}>
            {fullInvestigatorSheet ? (
              <div
                role="button"
                className="investigator-selection-sheet"
                aria-label={action.description || action.name}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                style={colorStyle}
                title={action.description || action.name}
                onClick={event => {
                  event.stopPropagation();
                  if (event.defaultPrevented || !event.currentTarget.contains(event.target as Node)) return;
                  const control = (event.target as Element).closest('button, a, input, select, textarea, summary, [role="dialog"]');
                  if (control && event.currentTarget.contains(control)) return;
                  if (!disabled) onExecuteAction(action.id);
                }}
                onKeyDown={event => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!disabled && !event.repeat) onExecuteAction(action.id);
                  }
                }}
              >
                <div className="investigator-selection-sheet-content" inert={digital ? undefined : true}>
                  <InvestigatorDisplay variant="reference" selectionPreview={digital && mobile} investigator={selectionInvestigator!} language={language} />
                </div>
              </div>
            ) : !hideActionButton && (
            <button
              data-game-action={action.id}
              onClick={(e) => { e.stopPropagation(); if (!disabled) onExecuteAction(action.id) }}
              disabled={disabled}
              className={`action-button${cardImage || heralds.length > 0 ? ' card-action' : ''}${action.color ? ' has-color' : ''}${selectionInvestigator ? ' investigator-selection-action' : ''}${ancientOneThumbnail ? ' ancient-one-selection-action' : ''}`}
              style={colorStyle}
              title={actionLabel}
              aria-label={compactCard ? actionLabel : undefined}
            >
                {actionIconKind(action).startsWith('setup-') && <ActionTypeIcon action={action} />}
                {selectionInvestigator && <div className="investigator-selection-marker" aria-hidden="true"><InvestigatorMarker investigator={selectionInvestigator} size={60} offsetByIndex={false} /></div>}
                {ancientOneThumbnail && <img key={ancientOneThumbnail} className="ancient-one-selection-thumbnail"
                  src={ancientOneThumbnail} alt="" width={60} height={60} loading="lazy" decoding="async"
                  onError={event => { event.currentTarget.style.display = 'none'; }} />}
                <div className="action-card-text">
                  <SimpleMarkdown text={(compactCard && action.shortLabel) || actionLabel} keyPrefix={`${action.id}-label`} wrapperClassName="action-name" />
                </div>
            </button>
            )}
                {!fullInvestigatorSheet && <div className={`game-action-preview${action.cardType === 'Monster' ? ' game-action-monster-preview' : ''}`}>
                {action.cardType === 'InvestigatorToken' && (
                  <div className="action-investigator-marker">
                    <img
                      loading="lazy"
                      src={getInvestigatorTokenImagePath(singleCard?.name || '', digital ? 'Digital' : 'Classic')}
                      alt={`${singleCard?.name} token`}
                    />
                  </div>
                )}
                {singleCard && kind && action.cardType !== 'Clue' && action.cardType !== 'Investigator' && (
                  <div className={singleCard?.used || singleCard?.exhausted ? (compactCard ? 'compact-card-used' : 'card-used') : ''}>
                    {compactCard && (singleCard?.used || singleCard?.exhausted) && <span className="compact-used-marker">{singleCard?.exhausted ? 'Exhausted' : 'Used'}</span>}
                    <SpellInvestigatorContext.Provider value={action.investigatorName}><SpellActionPreviewContext.Provider value={{ spellId: (singleCard as { id?: string }).id, preview: action.spellCastingPreview }}><ItemPurchaseContext.Provider value={action.itemPurchasePreview}><Card card={{
                      item: singleCard,
                      kind: kind,
                    }} /></ItemPurchaseContext.Provider></SpellActionPreviewContext.Provider></SpellInvestigatorContext.Provider>
                </div>)}
                {action.cardType === 'Monster' && (
                  <MonsterDetailCard monster={action.card as unknown as Monster} cls='monster-action' />
                )}
                {action.cardType === 'Clue' && clues !== 0 && (
                  <div className="clue-action">
                    <div className={`clues-sign ${clues < 0 ? 'minus' : 'plus'}`}>{clues < 0 ? "-" : "+"}</div>
                    &nbsp;&nbsp;
                    {Math.abs(clues) <= 1 ? (
                      <div className="clue-stack">
                        {Array.from({ length: Math.abs(clues) }).map((_, clueIdx) => (
                          <ArtworkImage loading="lazy" key={clueIdx} src="/Clue.jpg" alt="Clue" className="clue-token" />
                        ))}
                      </div>
                    ) : (
                      <div className='clue-action'>
                        <ArtworkImage loading="lazy" src="/Clue.jpg" alt="Clue" style={{ borderRadius: '50%' }} />
                        <span>{Math.abs(clues)}</span>
                      </div>
                    )}
                  </div>
                )}
                {terrorLevel > 0 && <TerrorLevelIncrease amount={terrorLevel} />}
                {action.cardType === 'BastToken' && bastTokenNumber !== 0 && (
                  <div className="clue-action">
                    <div className={`clues-sign ${bastTokenNumber < 0 ? 'minus' : 'plus'}`}>{bastTokenNumber < 0 ? "-" : "+"}</div>
                    &nbsp;&nbsp;
                    <BastToken />
                  </div>
                )}
                {mythosDetails && !!mythosDetails.name && (
                  <MythosBreakdown mythosDetails={mythosDetails} />
                )}
                {heralds.map(herald => (
                  <HeraldCard key={herald.name} herald={herald} />
                ))}
                {guardian && (
                  <GuardianCard guardian={guardian} />
                )}
                {action.cardType === 'Institution' && singleCard && (
                  <InstitutionDisplay institution={singleCard as Institution} />
                )}
                {cardImage && (
                  <img loading="lazy"
                    src={cardImage}
                    alt={action.name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                </div>}
              </div>
            {!fullInvestigatorSheet && action.cardType === 'Investigator' && action.card && <div className="game-action-investigator-summary"><strong>{singleCard?.name}</strong>
              {(action.card as Investigator).maxStamina > 0 ? <InvestigatorSummary investigator={action.card as Investigator} /> : <><p>Stamina {(action.card as Investigator).stamina} · Sanity {(action.card as Investigator).sanity} · Focus {(action.card as Investigator).focus}</p><SimpleMarkdown text={(action.card as Investigator).abilityDescription || ''} keyPrefix={`${action.id}-ability`} wrapperClassName="action-description" /></>}
            </div>}
            {action.card && action.cardType !== 'Investigator' && action.cardType !== 'Clue' && action.cardType !== 'TerrorLevel' && <div className="game-choice-summary" aria-disabled={disabled} onClick={selectPreview}>
              {kind && !(digital && ['skill', 'personalStory', 'relationship', 'madness', 'injury', 'corruption'].includes(kind)) && typeof getItemImagePath === 'function' && (() => {
                const classic = getItemImagePath(kind, singleCard?.name || '', (singleCard as { isNodensBlessing?: boolean })?.isNodensBlessing);
                const src = digital ? getDigitalItemArtworkPath(classic) : classic;
                return src ? <img src={src} alt="" onError={event => {event.currentTarget.hidden = true;}} /> : null;
              })()}
              <div><strong>{cardName}</strong>{singleCard && kind && typeof getDescription === 'function'
                ? <SpellInvestigatorContext.Provider value={action.investigatorName}><SpellActionPreviewContext.Provider value={{ spellId: (singleCard as { id?: string }).id, preview: action.spellCastingPreview }}><ActionCardDescription card={{ item: singleCard, kind }} digital={digital} /></SpellActionPreviewContext.Provider></SpellInvestigatorContext.Provider>
                : <SimpleMarkdown text={getChoiceDescription(action.card)} keyPrefix={`${action.id}-choice`} wrapperClassName="action-description" />}</div>
            </div>}
            {!previewOnly && action.card && action.cardType !== 'Clue' && action.cardType !== 'TerrorLevel' && <button className="game-action-details" onClick={() => setDetails(action)}>Details<span className="sr-only"> for {singleCard?.name || action.name}</span></button>}
            </article>
          );
        })}
      </div>
      {details && (details.cardType === 'Monster' ? <MonsterInspector monster={details.card as Monster} onClose={() => setDetails(null)} /> : details.cardType === 'Investigator' ? <InvestigatorInspector variant="reference" investigator={details.card as Investigator} language={language} onClose={() => setDetails(null)} /> : <GameDialog title={getCardName(details.card) || details.name} onClose={() => setDetails(null)}>
        <SimpleMarkdown text={details.description || ''} keyPrefix={`${details.id}-details`} wrapperClassName="action-description" />
        <button type="button" className="action-button game-action-detail-execute" disabled={loading || !!previewOnly || !currentDetailsAction || !!currentDetailsAction.disabled}
          onClick={event => {
            event.stopPropagation();
            if (loading || previewOnly || !currentDetailsAction || currentDetailsAction.disabled) return;
            setDetails(null);
            onExecuteAction(currentDetailsAction.id);
          }}>
          <SimpleMarkdown text={details.description || details.name} keyPrefix={`${details.id}-details-action`} wrapperClassName="action-name" />
        </button>
        <div className="game-action-detail-preview"><ActionsList previewOnly hideActionButton actions={[details]} loading={loading} language={language} onExecuteAction={() => {}} /></div>
      </GameDialog>)}
    </div>
  );
};
